import { getApi, postApi } from 'utils/Api';
import type {
  FilingDataListResponse,
  FilingDetails,
  Challan,
  Deductee,
  Salary,
  FilingError,
  ListFilingDataParams,
  GenerateFilingDataRequest,
  GetChallansParams,
  GetDeducteesParams,
  GetSalariesParams,
  GetErrorsParams,
  GenerateFilingDataResponse,
  DownloadUrlResponse,
  FilingStatus,
  ErrorSource,
  ErrorType,
  RecordType,
  PaginatedResponse,
} from './types/tdsFilingDataTypes';

export type {
  FilingStatus,
  FilingType,
  Quarter,
  ErrorSource,
  ErrorType,
  RecordType,
  PaginatedResponse,
  FilingDataListResponse,
  ParseSummary,
  FilingDataListItem,
  FilingDetails,
  Challan,
  Deductee,
  S16Record,
  C6ARecord,
  Salary,
  FilingError,
  ListFilingDataParams,
  GenerateFilingDataRequest,
  GetChallansParams,
  GetDeducteesParams,
  GetSalariesParams,
  GetErrorsParams,
  GenerateFilingDataResponse,
  DownloadUrlResponse,
} from './types/tdsFilingDataTypes';

export { TDS_FILING_DATA_QUERY_KEYS } from './types/tdsFilingDataTypes';

const tdsFilingData = {
  generateFiling: (data: GenerateFilingDataRequest) =>
    postApi<GenerateFilingDataResponse, GenerateFilingDataRequest>({
      url: '/admin/tds-filing-data/generate',
      responseSchema: {},
      requestData: data,
    }),

  listFilingData: (params: ListFilingDataParams) => {
    const query = new URLSearchParams();
    if (params.org_ids) query.set('org_ids', params.org_ids);
    if (params.merchant_ids) query.set('merchant_ids', params.merchant_ids);
    if (params.filing_type) query.set('filing_type', params.filing_type);
    if (params.quarter) query.set('quarter', params.quarter);
    if (params.fy) query.set('fy', params.fy);
    if (params.statuses) query.set('statuses', params.statuses);
    if (params.has_errors !== undefined) query.set('has_errors', String(params.has_errors));
    query.set('page', String(params.page ?? 1));
    query.set('page_size', String(params.page_size ?? 50));
    return getApi<FilingDataListResponse>({
      url: `/admin/tds-filing-data?${query.toString()}`,
      responseSchema: {},
    });
  },

  getStatusCounts: (params: Pick<ListFilingDataParams, 'org_ids' | 'filing_type' | 'quarter' | 'fy'>) => {
    const query = new URLSearchParams();
    if (params.org_ids) query.set('org_ids', params.org_ids);
    if (params.filing_type) query.set('filing_type', params.filing_type);
    if (params.quarter) query.set('quarter', params.quarter);
    if (params.fy) query.set('fy', params.fy);
    return getApi<{ status_counts: Partial<Record<FilingStatus, number>>; total: number }>({
      url: `/admin/tds-filing-data/status-counts?${query.toString()}`,
      responseSchema: {},
    });
  },

  getFilingDetails: (id: string) =>
    getApi<FilingDetails>({
      url: `/admin/tds-filing-data/${id}`,
      responseSchema: {},
    }),

  getChallans: (id: string, params: GetChallansParams = {}) => {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('page_size', String(params.page_size ?? 50));
    return getApi<PaginatedResponse<Challan>>({
      url: `/admin/tds-filing-data/${id}/challans?${query.toString()}`,
      responseSchema: {},
    });
  },

  getDeductees: (id: string, params: GetDeducteesParams = {}) => {
    const query = new URLSearchParams();
    if (params.challan_id) query.set('challan_id', params.challan_id);
    query.set('page', String(params.page ?? 1));
    query.set('page_size', String(params.page_size ?? 100));
    return getApi<PaginatedResponse<Deductee>>({
      url: `/admin/tds-filing-data/${id}/deductees?${query.toString()}`,
      responseSchema: {},
    });
  },

  getSalaries: (id: string, params: GetSalariesParams = {}) => {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('page_size', String(params.page_size ?? 50));
    return getApi<PaginatedResponse<Salary>>({
      url: `/admin/tds-filing-data/${id}/salaries?${query.toString()}`,
      responseSchema: {},
    });
  },

  getErrors: (id: string, params: GetErrorsParams = {}) => {
    const query = new URLSearchParams();
    if (params.error_source) query.set('error_source', params.error_source);
    if (params.error_type) query.set('error_type', params.error_type);
    if (params.record_type) query.set('record_type', params.record_type);
    query.set('page', String(params.page ?? 1));
    query.set('page_size', String(params.page_size ?? 50));
    return getApi<PaginatedResponse<FilingError>>({
      url: `/admin/tds-filing-data/${id}/errors?${query.toString()}`,
      responseSchema: {},
    });
  },

  getErrorFilterOptions: (id: string) =>
    getApi<{
      available_error_sources: ErrorSource[];
      available_error_types: ErrorType[];
      available_record_types: RecordType[];
    }>({
      url: `/admin/tds-filing-data/${id}/error-filter-options`,
      responseSchema: {},
    }),

  getDownloadUrl: (id: string, fileType: 'txt' | 'fvu' | '27A' | 'filing_ack' = 'txt') =>
    getApi<DownloadUrlResponse>({
      url: `/admin/tds-filing-data/${id}/download?type=${fileType}`,
      responseSchema: {},
    }),
};

export default tdsFilingData;
